import { Vector2, HexCoord } from '../types';
import { HexMath } from './HexMath';

/**
 * 统一坐标转换系统
 * 负责逻辑世界坐标、本地坐标、六边形坐标之间的转换
 * 统一处理浮点数精度问题
 */
export class CoordinateSystem {
    // 统一精度控制：保留 4 位小数
    private static readonly PRECISION = 4;

    /**
     * 四舍五入到统一精度
     */
    static round(value: number): number {
        const factor = Math.pow(10, this.PRECISION);
        return Math.round(value * factor) / factor;
    }

    /**
     * 四舍五入向量到统一精度
     */
    static roundVector(v: Vector2): Vector2 {
        return {
            x: this.round(v.x),
            y: this.round(v.y)
        };
    }

    /**
     * 世界坐标 -> 组本地坐标
     * @param worldPos 目标点的世界坐标
     * @param groupCenter 组中心的世界坐标
     * @param groupRotationDeg 组的旋转角度（角度制）
     */
    static worldToLocal(worldPos: Vector2, groupCenter: Vector2, groupRotationDeg: number): Vector2 {
        // 1. 平移到原点
        const translated = HexMath.sub(worldPos, groupCenter);
        // 2. 反向旋转
        const rotated = HexMath.rotate(translated, -groupRotationDeg);
        // 3. 精度处理
        return this.roundVector(rotated);
    }

    /**
     * 组本地坐标 -> 世界坐标
     * @param localPos 目标点的本地坐标
     * @param groupCenter 组中心的世界坐标
     * @param groupRotationDeg 组的旋转角度（角度制）
     */
    static localToWorld(localPos: Vector2, groupCenter: Vector2, groupRotationDeg: number): Vector2 {
        // 1. 旋转
        const rotated = HexMath.rotate(localPos, groupRotationDeg);
        // 2. 平移回世界位置
        const worldPos = HexMath.add(rotated, groupCenter);
        // 3. 精度处理
        return this.roundVector(worldPos);
    }

    /**
     * 六边形逻辑坐标 -> 逻辑世界坐标
     */
    static hexToWorld(coord: HexCoord, scale: number): Vector2 {
        const pos = HexMath.hexToPixel(coord, scale);
        return this.roundVector(pos);
    }

    /**
     * 逻辑世界坐标 -> 最近的六边形逻辑坐标
     */
    static worldToHex(worldPos: Vector2, scale: number): HexCoord {
        return HexMath.pixelToHex(worldPos, scale);
    }

    /**
     * 计算两个逻辑中心之间的理想偏移量（本地坐标系下）
     * 用于组内碎片的对齐
     */
    static getIdealOffset(pieceCoord: HexCoord, targetCoord: HexCoord, scale: number): Vector2 {
        const piecePos = HexMath.hexToPixel(pieceCoord, scale);
        const targetPos = HexMath.hexToPixel(targetCoord, scale);
        return this.roundVector(HexMath.sub(piecePos, targetPos));
    }
}
